import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useShallow } from "zustand/react/shallow";

import type { Market, Token } from "../api/types";
import type { TargetChoice } from "../lib/accounts";
import type { SellPlan } from "../lib/holdings";
import { decimalsForTick, format, parse, toFixed, type Micro } from "../lib/money";
import { formatSigned, sellPnl, type SellFraction } from "../lib/position";
import {
  actionLabel,
  bookProblem,
  quotePrice,
  resolveQuick,
  type QuickAction,
  type Resolved,
} from "../lib/quick";
import { rowMatches, type QuickRow } from "../lib/quickrows";
import { useBooks } from "../store/useBooks";
import { AccountDetails } from "./AccountDetails";

/**
 * Таблица быстрых действий: строка — аккаунт или когорта, колонки — исходы.
 *
 * # Почему таблица
 *
 * Замечание владельца 2026-10-07: быстрые действия должны быть быстрыми и при
 * нескольких аккаунтах, то есть нажатие — сразу в строке нужного аккаунта, без
 * переключения цели. Раскладку выбрал владелец из двух: таблица плотнее
 * карточек, и три аккаунта с двумя когортами — это пять строк высотой в кнопку.
 *
 * # Подробность кнопок — по ширине
 *
 * Второе замечание того же дня: на широком экране место есть, и кнопке есть
 * что сказать — цену, share, комиссию, прибыль закрытия, как говорили прежние
 * большие кнопки. На узком тот же текст не помещается, а горизонтальная
 * прокрутка у панели, по которой бьют в спешке, хуже обрезанной подсказки.
 * Поэтому уровней три (Detail), и таблица выбирает самый подробный из тех,
 * что ВЛЕЗАЮТ (useFit): меряет себя, а не угадывает по ширине экрана, —
 * ширину съедают и число исходов, и появившиеся позиции.
 *
 * # Подписка на стаканы
 *
 * Здесь, а не в панели: таблица — единственное, что перерисовывается на каждую
 * дельту стакана своих двух токенов. Позиции и планы закрытия от стакана не
 * зависят и приходят готовыми.
 */

/** Позиция строки по исходу: что показать в ячейке и чем закрывать. */
export interface QuickCell {
  /** План закрытия выбранной долей по аккаунтам строки. */
  plan: SellPlan;
  /** Набранное, share; 0 — позиции нет. */
  shares: Micro;
  /** Средняя цена покупок строкой; null — неизвестна. */
  avg: string | null;
  /** Откуда число: журнал аккаунтов строки или снимок их кошельков. */
  source: "fills" | "snapshot" | null;
  /** Снимок кошельков строки, share; null — неизвестен. */
  indexed: string | null;
  /** Рынок рассчитан — позицию гасят, а не продают. */
  redeemable: boolean;
}

export interface QuickRowData {
  /** asset_id → ячейка. */
  cells: Map<string, QuickCell>;
  /** Почему позиция строки неизвестна; null — известна. */
  unknown: string | null;
  /** Открытых ордеров строки по этому рынку — по сведениям вкладки. */
  openOrders: number;
}

type At = number;

/**
 * Уровень подробности кнопок.
 *
 *   - `compact` — буквы M/T, цены в шапке колонки (узкая панель);
 *   - `mid` — кнопка в две строки: цена, share и комиссия покупки, цена и
 *     прибыль закрытия;
 *   - `full` — всё, что было на прежних больших кнопках: сумма, share,
 *     комиссия, выручка и прибыль закрытия.
 */
export type Detail = "compact" | "mid" | "full";

/**
 * Ширины уровня — ПОСТОЯННЫЕ, не по тексту.
 *
 * Текст кнопки меняется с каждой котировкой: share, комиссия, прибыль. Кнопка
 * по ширине текста тянула за собой колонку, колонка — весь ряд, и нажимать по
 * скачущей мишени нельзя. Поэтому ширина задана местом под худший обычный
 * случай; что не влезло — обрезается многоточием, но никогда не обрезаются
 * комиссия и прибыль (Act), а полный расчёт всегда в подсказке.
 *
 * Единица — `ch` моноширинного шрифта цифр (.num): у кнопки это символ 11px,
 * у позиции — 12px. Отступы и рамка кнопки — сверху (0.75rem + 2px).
 *
 *   - mid, покупка: «1666.66 sh −.0070» — 15ch;
 *   - mid, закрытие: «M .58» над «+12.34?» — 8ch;
 *   - full, покупка: «$5 ×1 · 166.66 sh −$0.33» — 23ch;
 *   - full, закрытие: «9.61 sh ≈ $5.57 +$12.34» — 22ch;
 *   - позиция: «166.66 @.520 −5», в узкой ещё и « +12.34».
 */
export const WIDTH: Record<Detail, { buy?: string; close?: string; pos: string }> = {
  compact: { pos: "22ch" },
  mid: { buy: "calc(15ch + 0.75rem + 2px)", close: "calc(8ch + 0.75rem + 2px)", pos: "15ch" },
  full: { buy: "calc(23ch + 0.75rem + 2px)", close: "calc(22ch + 0.75rem + 2px)", pos: "15ch" },
};

interface Props {
  market: Market;
  /** Исходы В ПОРЯДКЕ outcome_index: порядок задаёт колонки и клавиши. */
  tokens: Token[];
  rows: QuickRow[];
  data: Map<string, QuickRowData>;
  /** Активная строка — от неё бьют клавиши. */
  active: TargetChoice | null;
  /** Сумма покупки; null — не выбрана или не разобрана. */
  usd: string | null;
  fraction: SellFraction;
  /** Клавиши взведены: подсказки клавиш в шапке нужны только тогда. */
  armed: boolean;
  onBuy: (row: QuickRow, action: QuickAction, assetId: string, at: At) => void;
  onSell: (row: QuickRow, action: QuickAction, assetId: string, at: At) => void;
  onCancel: (row: QuickRow, at: At) => void;
  onActivate: (row: QuickRow) => void;
  /**
   * Строки другой площадки в конце таблицы (строка PF, ADR-0040). Функцией
   * от подробности: кнопки обязаны совпадать по ширине с кнопками аккаунтов.
   */
  extra?: ((detail: Detail, span: number) => ReactNode) | undefined;
}

/** Клавиши колонок: ряд исхода на клавиатуре — Q/W над A/S (lib/hotkeys.ts). */
const KEYS: ReadonlyArray<readonly [string, string]> = [
  ["Q", "W"],
  ["A", "S"],
];

const BUY_JOIN: QuickAction = { side: "BUY", mode: "join" };
const BUY_CROSS: QuickAction = { side: "BUY", mode: "cross" };
const SELL_JOIN: QuickAction = { side: "SELL", mode: "join" };
const SELL_CROSS: QuickAction = { side: "SELL", mode: "cross" };

type Book = Parameters<typeof resolveQuick>[1]["book"];

interface Quote {
  problem: string | null;
  buyJoin: Resolved | null;
  buyCross: Resolved | null;
  priceBuyJoin: ReturnType<typeof quotePrice>;
  priceBuyCross: ReturnType<typeof quotePrice>;
  priceSellJoin: ReturnType<typeof quotePrice>;
  priceSellCross: ReturnType<typeof quotePrice>;
  book: Book;
}

/** Что нужно ячейкам строки, кроме самой строки. */
interface Ctx {
  market: Market;
  tokens: Token[];
  quotes: Quote[];
  digits: number;
  detail: Detail;
  fraction: SellFraction;
}

export function QuickTable(props: Props) {
  const { market, tokens, rows, data, usd, armed } = props;
  // Знаков у цены — по шагу рынка: «.40», а не «.4», иначе соседние цены
  // разной длины и читаются как разные по точности.
  const digits = decimalsForTick(market.tick_size);
  const books = useBooks(useShallow((s) => tokens.map((t) => s.books[t.asset_id])));

  // Покупка у всех строк одна и та же — пресет на аккаунт (у когорты — на
  // КАЖДЫЙ, Д-5), поэтому считается один раз на исход.
  const quotes: Quote[] = tokens.map((t, i) => {
    const book = books[i];
    const buy = (a: QuickAction): Resolved | null =>
      usd === null ? null : resolveQuick(a, { market, assetId: t.asset_id, book, usd });
    return {
      problem: bookProblem(book),
      buyJoin: buy(BUY_JOIN),
      buyCross: buy(BUY_CROSS),
      priceBuyJoin: quotePrice(BUY_JOIN, market, book),
      priceBuyCross: quotePrice(BUY_CROSS, market, book),
      priceSellJoin: quotePrice(SELL_JOIN, market, book),
      priceSellCross: quotePrice(SELL_CROSS, market, book),
      book,
    };
  });

  // Подбор подробности перемеряется, только когда меняется то, что двигает
  // ширину: строки и появление позиций (у строки с позицией есть кнопки
  // закрытия, у пустой — нет). Котировки, сумма и есть ли у кнопки цена сюда
  // НЕ входят: ширины кнопок и ячеек постоянны (WIDTH), а пересчёт по ним
  // переключал уровень посреди окна — и ряд прыгал.
  const structure =
    rows.map((r) => r.key).join(",") +
    "|" +
    rows
      .map((r) =>
        tokens
          .map((t) => {
            const plan = data.get(r.key)?.cells.get(t.asset_id)?.plan;
            return plan && (plan.shares === null || Number(plan.shares) > 0) ? 1 : 0;
          })
          .join(""),
      )
      .join(",");
  const { wrapRef, tableRef, detail } = useFit(structure);

  const ctx: Ctx = { market, tokens, quotes, digits, detail, fraction: props.fraction };
  const span = 2 + tokens.length * 3;
  const terse = detail === "compact";

  return (
    <div ref={wrapRef} className="overflow-x-auto">
      {/* По ширине содержимого, а не во всю панель, на любом уровне.
          Растянутая таблица делила свободное место между колонками по длине
          их текста — и с каждой котировкой («5.05 sh» → «166.66 sh») колонки
          переезжали, а за ними весь ряд (замечание владельца 2026-10-07).
          Ширина кнопок и ячеек позиции задана уровнем (WIDTH), поэтому от
          котировок не зависит. */}
      <table ref={tableRef} className="border-separate border-spacing-0 text-xs">
        <thead className="text-[var(--color-muted)]">
          <tr>
            <th rowSpan={2} className="pr-2 pb-0.5 text-left align-bottom font-normal">
              аккаунт
            </th>
            {tokens.map((t, i) => (
              <th
                key={t.asset_id}
                colSpan={3}
                className="border-l border-[var(--color-border)] px-2 pb-0.5 text-left font-medium"
                style={{ color: outcomeColor(i) }}
              >
                {(t.outcome || `исход ${t.outcome_index}`).toUpperCase()}
                {quotes[i]?.problem && (
                  <span className="ml-2 font-normal text-amber-300" title={quotes[i]!.problem!}>
                    {quotes[i]!.problem}
                  </span>
                )}
              </th>
            ))}
            <th rowSpan={2} />
          </tr>
          <tr>
            {tokens.map((t, i) => {
              const q = quotes[i]!;
              const keys = KEYS[i] ?? ["", ""];
              const k = (shift: boolean, n: 0 | 1) =>
                armed && keys[n] ? `${shift ? "⇧" : ""}${keys[n]}` : "";
              // В узкой раскладке цены стоят здесь, один раз на все строки:
              // кнопкам остаются буквы. В подробной цены на самих кнопках, а
              // здесь — названия колонок и клавиши.
              return [
                <th key={`${t.asset_id}:b`} className="border-l border-[var(--color-border)] px-2 pb-1 text-left align-bottom font-normal whitespace-nowrap">
                  {terse ? (
                    // Цены — под названием, а не рядом: в одну строку шапка
                    // была шире кнопок под ней и выталкивала таблицу за край
                    // узкой панели.
                    <span className="flex flex-col">
                      <span>купить</span>
                      <span>
                        <QuoteLabel mode="M" q={q.priceBuyJoin} digits={digits} hotkey={k(false, 0)} />{" "}
                        <QuoteLabel mode="T" q={q.priceBuyCross} digits={digits} hotkey={k(false, 1)} />
                      </span>
                    </span>
                  ) : (
                    <>
                      купить <Keys a={k(false, 0)} b={k(false, 1)} />
                    </>
                  )}
                </th>,
                <th key={`${t.asset_id}:p`} className="px-2 pb-1 text-right align-bottom font-normal">
                  позиция
                </th>,
                <th key={`${t.asset_id}:s`} className="px-2 pb-1 text-left align-bottom font-normal whitespace-nowrap">
                  {terse ? (
                    <span className="flex flex-col">
                      <span>закрыть</span>
                      <span>
                        <QuoteLabel mode="M" q={q.priceSellJoin} digits={digits} hotkey={k(true, 0)} />{" "}
                        <QuoteLabel mode="T" q={q.priceSellCross} digits={digits} hotkey={k(true, 1)} />
                      </span>
                    </span>
                  ) : (
                    <>
                      закрыть{" "}
                      <span className="num inline-block" style={{ width: "4ch" }}>
                        {props.fraction}%
                      </span>{" "}
                      <Keys a={k(true, 0)} b={k(true, 1)} />
                    </>
                  )}
                </th>,
              ];
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            row.kind === "group" ? (
              <tr key={row.key}>
                <td
                  colSpan={span}
                  className="border-t border-[var(--color-border)] pt-1.5 pb-0.5 text-[10px] uppercase tracking-wide text-[var(--color-muted)]"
                >
                  {row.label} · {row.note}
                </td>
              </tr>
            ) : (
              <RowView key={row.key} row={row} ctx={ctx} {...props} />
            ),
          )}
          {props.extra?.(detail, span)}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Самый подробный уровень, который влезает в ширину панели.
 *
 * Меряется сама таблица, а не экран: ширину съедают число исходов, позиции и
 * длина имён. На новом ключе (ширина панели или структура строк) пробуется
 * `full`, и пока таблица шире обёртки — уровень понижается. Всё это внутри
 * useLayoutEffect, то есть до отрисовки: оператор не видит, как кнопки
 * перескакивают.
 *
 * Цены при подборе не перемеряются намеренно: ширины кнопок постоянны
 * (WIDTH), а чтение ширины на каждой дельте стакана заставляло бы браузер
 * пересчитывать раскладку страницы десятки раз в секунду.
 */
function useFit(structure: string): {
  wrapRef: RefObject<HTMLDivElement | null>;
  tableRef: RefObject<HTMLTableElement | null>;
  detail: Detail;
} {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [width, setWidth] = useState(0);
  const [fit, setFit] = useState<{ key: string; detail: Detail }>({ key: "", detail: "full" });

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setWidth(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const key = `${width}|${structure}`;
  const detail: Detail = fit.key === key ? fit.detail : "full";
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return;
    const over = table.scrollWidth > wrap.clientWidth + 1;
    if (over && detail !== "compact") {
      setFit({ key, detail: detail === "full" ? "mid" : "compact" });
    } else if (fit.key !== key || fit.detail !== detail) {
      setFit({ key, detail });
    }
  }, [key, detail, fit.key, fit.detail]);

  return { wrapRef, tableRef, detail };
}

function RowView({
  row,
  ctx,
  data,
  active,
  onBuy,
  onSell,
  onCancel,
  onActivate,
}: Props & { row: QuickRow; ctx: Ctx }) {
  const { tokens, quotes } = ctx;
  const d = data.get(row.key);
  const isActive = rowMatches(row, active);
  const cohort = row.kind === "cohort";
  const members = row.accounts.length;
  const reason = row.buyProblem ?? row.sellProblem ?? row.cancelProblem;
  // Карточка — только у строки ОДНОГО аккаунта: у когорты баланса и тира нет,
  // биржа считает их по кошельку.
  const funder = row.kind === "account" ? (row.accounts[0] ?? null) : null;
  const [details, setDetails] = useState(false);

  return (
    <tr
      className={cohort ? "bg-white/[0.03]" : undefined}
      style={
        isActive
          ? { backgroundColor: "color-mix(in srgb, var(--color-accent) 14%, transparent)" }
          : undefined
      }
    >
      <td className="border-t border-[var(--color-border)] py-0.5 pr-2">
        <div className="flex items-start gap-1">
          {/* Имя — кнопка: щелчок делает строку активной, и от неё начинают
              бить клавиши. Отдельного селектора на панели больше нет — выбор
              там же, где кнопки. */}
          <button
            type="button"
            onClick={() => onActivate(row)}
            className={`flex items-start gap-1 text-left whitespace-nowrap ${row.nested ? "pl-3" : ""}`}
            title={
              (isActive
                ? "активная строка: от неё клавиши, взятие сигналов и тир"
                : "сделать строку активной: от неё будут бить клавиши и браться сигналы") +
              (cohort ? ". Когорта: покупка — полным пресетом с КАЖДОГО годного аккаунта" : "") +
              (reason ? `. ${reason}` : "")
            }
          >
            <span
              className="w-2 shrink-0 text-[var(--color-accent)]"
              aria-label={isActive ? "активная строка" : undefined}
            >
              {isActive ? "●" : ""}
            </span>
            {/* Причина — СЛОВАМИ, а не знаком с подсказкой: у погашенной кнопки
                подсказку браузер может не показать вовсе. Под именем, а не рядом:
                рядом она отнимала у кнопок до 130 px и выталкивала таблицу за
                край панели даже в узкой раскладке. */}
            <span className="flex flex-col">
              <span className="flex items-baseline gap-1">
                <span className={cohort ? "font-medium" : ""}>{row.label}</span>
                {cohort && row.note && (
                  <span
                    className="text-[var(--color-muted)]"
                    title={`поставят полный пресет: ${row.note.slice(1)} из ${members}`}
                  >
                    {row.note}
                  </span>
                )}
              </span>
              {reason && <span className="text-[10px] text-amber-300">{shortReason(reason)}</span>}
            </span>
          </button>
          {/* Детали — отдельной кнопкой, а не по щелчку на имя: щелчок по имени
              делает строку активной, и смешивать выбор цели с просмотром значило
              бы переключать боевую цель ради взгляда на баланс. */}
          {funder && (
            <button
              type="button"
              aria-label="детали"
              title="детали: баланс, оборот, уровень"
              onClick={() => setDetails(true)}
              className="text-[var(--color-muted)] hover:text-[var(--color-accent)]"
            >
              ⓘ
            </button>
          )}
        </div>
        {details && funder && (
          <AccountDetails slot={row.label} funder={funder} onClose={() => setDetails(false)} />
        )}
      </td>

      {tokens.map((t, i) => {
        const q = quotes[i]!;
        const cell = d?.cells.get(t.asset_id);
        const color = outcomeColor(i);
        return [
          <td key={`${t.asset_id}:b`} className="border-t border-l border-[var(--color-border)] px-2 py-0.5">
            <span className="flex gap-1">
              <BuyButton
                row={row}
                ctx={ctx}
                action={BUY_JOIN}
                color={color}
                r={q.buyJoin}
                price={q.priceBuyJoin}
                outcome={t.outcome}
                onClick={(e) => onBuy(row, BUY_JOIN, t.asset_id, e.timeStamp || performance.now())}
              />
              <BuyButton
                row={row}
                ctx={ctx}
                action={BUY_CROSS}
                color={color}
                r={q.buyCross}
                price={q.priceBuyCross}
                outcome={t.outcome}
                onClick={(e) => onBuy(row, BUY_CROSS, t.asset_id, e.timeStamp || performance.now())}
              />
            </span>
          </td>,
          <td key={`${t.asset_id}:p`} className="border-t border-[var(--color-border)] px-2 py-0.5 text-right">
            <PositionView
              cell={cell}
              unknown={d?.unknown ?? null}
              ctx={ctx}
              assetId={t.asset_id}
              book={q.book}
            />
          </td>,
          <td key={`${t.asset_id}:s`} className="border-t border-[var(--color-border)] px-2 py-0.5">
            <CloseButtons
              row={row}
              cell={cell}
              unknown={d?.unknown ?? null}
              ctx={ctx}
              q={q}
              assetId={t.asset_id}
              outcome={t.outcome}
              onSell={(a, at) => onSell(row, a, t.asset_id, at)}
            />
          </td>,
        ];
      })}

      <td className="border-t border-l border-[var(--color-border)] py-0.5 pl-2 text-right">
        <Act
          detail="compact"
          mode={d && d.openOrders > 0 ? `✕${d.openOrders}` : "✕"}
          color="var(--color-sell)"
          disabled={row.cancelProblem !== null}
          title={
            row.cancelProblem ??
            `снять ордера ${cohort ? "когорты " : ""}${row.label} по этому рынку` +
              (d && d.openOrders > 0 ? ` (открытых во вкладке: ${d.openOrders})` : "")
          }
          onClick={(e) => onCancel(row, e.timeStamp || performance.now())}
        />
      </td>
    </tr>
  );
}

/** Цена уровня в шапке узкой раскладки: «M .57 Q». */
function QuoteLabel({
  mode,
  q,
  digits,
  hotkey,
}: {
  mode: "M" | "T";
  q: ReturnType<typeof quotePrice>;
  digits: number;
  hotkey: string;
}) {
  return (
    <span className="num" title={q.ok ? undefined : q.reason}>
      <span className="text-[var(--color-fg)]">{mode}</span>{" "}
      {/* Постоянной ширины: «—» без стакана и «.57» с ним иначе двигали бы
          колонку, а за ней кнопки под шапкой. */}
      <span className="inline-block text-left" style={{ width: `${digits + 1}ch` }}>
        {q.ok ? shortPrice(q.price, digits) : "—"}
      </span>
      {hotkey && <span className="kbd kbd-sm ml-0.5">{hotkey}</span>}
    </span>
  );
}

/** Клавиши колонки в шапке подробной раскладки — только когда взведены. */
function Keys({ a, b }: { a: string; b: string }) {
  if (!a && !b) return null;
  return (
    <span>
      {a && <span className="kbd kbd-sm">{a}</span>} {b && <span className="kbd kbd-sm">{b}</span>}
    </span>
  );
}

function BuyButton({
  row,
  ctx,
  action,
  color,
  r,
  price,
  outcome,
  onClick,
}: {
  row: QuickRow;
  ctx: Ctx;
  action: QuickAction;
  color: string;
  r: Resolved | null;
  price: ReturnType<typeof quotePrice>;
  outcome: string;
  onClick: (e: MouseEvent) => void;
}) {
  const head = `${row.label} · ${actionLabel(action, outcome)}`;
  let title: string;
  let disabled = true;
  if (row.buyProblem !== null) title = `${head}: ${row.buyProblem}`;
  else if (r === null) title = `${head}: не выбран размер`;
  else if (!r.ok) title = `${head}: ${r.reason}`;
  else {
    disabled = false;
    title =
      `${head} ${r.price} × $${r.usd} (${r.size} share)` +
      feeTail(r) +
      (row.kind === "cohort"
        ? ` — с КАЖДОГО годного аккаунта (${row.note ?? ""}), не делится между ними`
        : "");
  }

  // Расчёт показывается и у погашенной строки — по нему выбирают; гаснет
  // только нажатие. Сумма у всех строк одна и стоит в шапке панели, поэтому в
  // средней раскладке на кнопке её нет, а share есть: от цены зависят они.
  let sub = "";
  let tail = "";
  if (r?.ok) {
    const fee = r.fee === null ? null : parse(r.fee);
    const charged = fee !== null && fee > 0;
    if (ctx.detail === "full") {
      sub = `$${r.usd}${row.kind === "cohort" ? ` ${row.note ?? ""}` : ""} · ${r.size} sh`;
      tail = r.fee === null ? "?" : charged ? `−$${feeText(r.fee)}` : "";
    } else {
      sub = `${r.size} sh`;
      tail = r.fee === null ? "?" : charged ? `−${feeText(r.fee).replace(/^0\./, ".")}` : "";
    }
  }

  return (
    <Act
      detail={ctx.detail}
      mode={modeName(action, ctx.detail)}
      price={price.ok ? shortPrice(price.price, ctx.digits) : "—"}
      sub={sub}
      tail={tail}
      tailColor="var(--color-muted)"
      width={WIDTH[ctx.detail].buy}
      color={color}
      filled
      disabled={disabled}
      title={title}
      onClick={onClick}
    />
  );
}

/** Хвост подсказки о комиссии — те же слова, что были у больших кнопок. */
function feeTail(r: Extract<Resolved, { ok: true }>): string {
  if (r.fee === null) return " · комиссия неизвестна: расписание рынка не сообщено";
  const fee = parse(r.fee);
  if (fee !== null && fee > 0) return ` · комиссия ${r.fee} USD, итого ${r.usdNet} USD`;
  return " · комиссии нет: ордер встаёт в стакан мейкером";
}

/** Прибыль закрытия плана по цене режима, после комиссии; reason — не из чего. */
function closePnl(
  cell: QuickCell,
  market: Market,
  assetId: string,
  book: Book,
  action: QuickAction,
): { r: Extract<Resolved, { ok: true }>; net: Micro | null; gross: Micro | null } | { reason: string } {
  if (cell.plan.size === null) return { reason: cell.plan.problem ?? "закрывать нечего" };
  const r = resolveQuick(action, { market, assetId, book, shares: cell.plan.size });
  if (!r.ok) return { reason: r.reason };
  if (!cell.avg) return { r, net: null, gross: null };
  const gross = sellPnl({ price: r.price, size: r.size, avgPrice: cell.avg });
  const net =
    r.fee === null ? null : sellPnl({ price: r.price, size: r.size, avgPrice: cell.avg, fee: r.fee });
  return { r, net, gross };
}

function PositionView({
  cell,
  unknown,
  ctx,
  assetId,
  book,
}: {
  cell: QuickCell | undefined;
  unknown: string | null;
  ctx: Ctx;
  assetId: string;
  book: Book;
}) {
  // Коробка постоянной ширины — та же причина, что у кнопок: «—» и «166.66
  // @.520 +1.20» в одной колонке иначе двигали бы её с каждым филлом.
  const box = (children: ReactNode, title?: string) => (
    <span
      className="num inline-block overflow-hidden text-right whitespace-nowrap"
      style={{ width: WIDTH[ctx.detail].pos }}
      title={title}
    >
      {children}
    </span>
  );
  if (!cell || cell.shares <= 0) {
    return unknown && cell?.plan.shares === null
      ? box(<span className="text-amber-300">?</span>, unknown)
      : box(<span className="text-[var(--color-muted)]">—</span>);
  }
  // Деньги — по ТЕЙКЕРУ: это цена выхода прямо сейчас, а мейкерская — цена,
  // которую ещё надо дождаться. В подробной раскладке прибыль стоит на самих
  // кнопках закрытия, и здесь её нет — только в подсказке.
  const exit = closePnl(cell, ctx.market, assetId, book, SELL_CROSS);
  const net = "reason" in exit ? null : exit.net;
  const lag =
    cell.indexed !== null && cell.source === "fills" && parse(cell.indexed) !== cell.shares
      ? `снимок кошельков показывает ${cell.indexed} — индексатор отстаёт от наших филлов`
      : null;
  const title = [
    `${format(cell.shares)} share${cell.avg ? ` по средней ${cell.avg}` : ""}`,
    cell.source === "fills" ? "по нашим филлам" : "по снимку кошельков",
    lag,
    cell.plan.basis === "fills" ? "закрытие посчитано по журналу: снимок пуст" : null,
    Number(cell.plan.reserved) > 0 ? `в заявках на продажу: ${cell.plan.reserved}` : null,
    cell.plan.tail ? `хвост ${cell.plan.tail} мельче шага размера — не продаётся` : null,
    net !== null ? `чистыми при закрытии ${ctx.fraction}% тейкером: ${formatSigned(net)}` : null,
    cell.redeemable ? "рынок рассчитан — позиция погашается" : null,
  ]
    .filter(Boolean)
    .join("\n");

  return box(
    <>
      <span className={cell.plan.basis === "fills" ? "text-amber-300" : ""}>{format(cell.shares)}</span>
      {cell.avg && <span className="text-[var(--color-muted)]"> @{shortPrice(cell.avg, 3)}</span>}
      {Number(cell.plan.reserved) > 0 && <span className="text-amber-300"> −{cell.plan.reserved}</span>}
      {cell.redeemable ? (
        <span className="text-amber-300"> погаш.</span>
      ) : (
        ctx.detail === "compact" &&
        net !== null && <span style={{ color: pnlColor(net) }}> {formatSigned(net)}</span>
      )}
    </>,
    title,
  );
}

function CloseButtons({
  row,
  cell,
  unknown,
  ctx,
  q,
  assetId,
  outcome,
  onSell,
}: {
  row: QuickRow;
  cell: QuickCell | undefined;
  unknown: string | null;
  ctx: Ctx;
  q: Quote;
  assetId: string;
  outcome: string;
  onSell: (a: QuickAction, at: At) => void;
}) {
  const plan = cell?.plan;
  // Кнопок нет, когда ТОЧНО известно, что закрывать нечего: две погашенные
  // кнопки в каждой пустой строке — шум. Неизвестная позиция — кнопки есть, но
  // выключены с причиной (то же различие «неизвестно ≠ ноль», что и раньше).
  if (!plan || (plan.shares !== null && Number(plan.shares) <= 0)) return null;
  if (cell?.redeemable) return null;

  const button = (action: QuickAction) => {
    const head = `${row.label} · ${actionLabel(action, outcome)} ${ctx.fraction}%`;
    const price = action.mode === "join" ? q.priceSellJoin : q.priceSellCross;
    let title: string;
    let disabled = true;
    let sub = "";
    let tail = "";
    let tailColor = "var(--color-muted)";
    if (row.sellProblem !== null) title = `${head}: ${row.sellProblem}`;
    else if (plan.legs.length === 0) title = `${head}: ${plan.problem ?? unknown ?? "закрывать нечего"}`;
    else {
      const exit = closePnl(cell!, ctx.market, assetId, q.book, action);
      if ("reason" in exit) title = `${head}: ${exit.reason}`;
      else {
        disabled = false;
        const legs =
          plan.legs.length > 1
            ? ` — по аккаунтам: ${plan.legs.map((l) => l.shares).join(" + ")}`
            : "";
        title =
          `${head} ${exit.r.price} × ${exit.r.size} share (≈ $${cents(exit.r.usd)})${legs}` +
          feeTail(exit.r) +
          (exit.net !== null
            ? ` · P&L ${formatSigned(exit.net)} после комиссии`
            : exit.gross !== null
              ? ` · P&L ${formatSigned(exit.gross)} до комиссии (комиссия неизвестна)`
              : " · P&L —: средняя входа неизвестна") +
          (plan.problem ? `\n${plan.problem}` : "");
        // Прибыль — главное число кнопки закрытия: ради него на неё и смотрят.
        // Чистая, после комиссии; неизвестная комиссия — грязная со знаком «?».
        const pnl = exit.net ?? exit.gross;
        tail = pnl === null ? "P&L —" : `${formatSigned(pnl)}${exit.net === null ? "?" : ""}`;
        tailColor = pnl === null ? "var(--color-muted)" : pnlColor(pnl);
        if (ctx.detail === "full") sub = `${exit.r.size} sh ≈ $${cents(exit.r.usd)}`;
      }
    }
    return (
      <Act
        detail={ctx.detail}
        mode={modeName(action, ctx.detail)}
        price={price.ok ? shortPrice(price.price, ctx.digits) : "—"}
        sub={sub}
        tail={tail}
        tailColor={tailColor}
        width={WIDTH[ctx.detail].close}
        color="var(--color-sell)"
        disabled={disabled}
        title={title}
        onClick={(e) => onSell(action, e.timeStamp || performance.now())}
      />
    );
  };

  return (
    <span className="flex items-center gap-1">
      {button(SELL_JOIN)}
      {button(SELL_CROSS)}
      {/* Пропуск части аккаунтов когорты — знаком, а причина — в подсказке:
          закрытие уйдёт, но не со всех кошельков, и знать это надо до нажатия. */}
      {plan.legs.length > 0 && plan.problem && (
        <span className="text-amber-300" title={plan.problem}>
          !
        </span>
      )}
    </span>
  );
}

/** Подпись режима на кнопке: буква в узкой и средней раскладке, слово в широкой. */
function modeName(action: QuickAction, detail: Detail): string {
  if (detail === "full") return action.mode === "join" ? "Maker" : "Taker";
  return action.mode === "join" ? "M" : "T";
}

/**
 * Кнопка строки.
 *
 * Узкая — буква; средняя и широкая — две строки: режим с ценой и расчёт под
 * ними. Хвост (комиссия, прибыль) красится своим цветом: знак прибыли —
 * единственное, ради чего на него смотрят, и покрасить его в красное «потому
 * что продажа» значило бы спрятать ровно то, что показываем.
 */
export function Act({
  detail,
  mode,
  price,
  sub,
  tail,
  tailColor,
  width,
  color,
  filled,
  disabled,
  title,
  onClick,
}: {
  detail: Detail;
  mode: string;
  price?: string;
  sub?: string;
  tail?: string;
  tailColor?: string;
  /** Постоянная ширина уровня (WIDTH); у буквенной кнопки не нужна. */
  width?: string | undefined;
  color: string;
  filled?: boolean;
  disabled: boolean;
  title: string;
  onClick: (e: MouseEvent) => void;
}) {
  const style = {
    borderColor: color,
    color,
    ...(filled && !disabled
      ? { backgroundColor: `color-mix(in srgb, ${color} 16%, transparent)` }
      : {}),
  };
  if (detail === "compact") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        className="num h-6 min-w-7 rounded border px-1.5 text-[11px] leading-none font-medium disabled:opacity-30"
        style={style}
      >
        {mode}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="num flex shrink-0 flex-col items-start overflow-hidden rounded border px-1.5 py-0.5 text-left text-[11px] leading-tight whitespace-nowrap disabled:opacity-35"
      style={{ ...style, width }}
    >
      <span className="max-w-full truncate font-medium">
        {mode} {price}
      </span>
      {/* Вторая строка есть всегда, даже пустая: иначе кнопки строки разной
          высоты, и ряд прыгает, когда у позиции появляется прибыль. Не
          влезает — обрезается подробность, а хвост (комиссия, прибыль) стоит
          целиком: ради него на кнопку и смотрят. */}
      <span className="flex min-h-[1.25em] w-full min-w-0 gap-1 text-[10px] text-[var(--color-muted)]">
        <span className="min-w-0 truncate">{sub}</span>
        {tail && (
          <span className="shrink-0" style={{ color: tailColor }}>
            {tail}
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * Цвет исхода: верхний зелёный, нижний красный.
 *
 * Цвет означает ИСХОД, а не сторону сделки: на «Bitcoin Up or Down» это
 * совпадает с графиком — вверх зелёное, вниз красное. Номер — по положению
 * колонки, тем же счётчиком, что раздаёт клавиши Q/W и A/S. Третьему исходу
 * «вверх/вниз» не подходит — нейтральный акцент.
 */
export function outcomeColor(index: number): string {
  if (index === 0) return "var(--color-buy)";
  if (index === 1) return "var(--color-sell)";
  return "var(--color-accent)";
}

/**
 * Причина строки — коротко, чтобы встать в колонку имени: полная фраза
 * остаётся в подсказках кнопок.
 */
function shortReason(reason: string): string {
  if (reason.startsWith("не загружен трейдером")) {
    return reason.includes("осушается") ? "не загружен, осушается" : "не загружен";
  }
  if (reason.startsWith("осушается")) return "осушается: только закрыть";
  if (reason === "в когорте нет аккаунтов для постановки") return "нет годных";
  if (reason === "в когорте нет аккаунтов из состава") return "пуста";
  return reason;
}

/** «0.57» → «.57»: в шапке и на кнопках цена стоит десятки раз. */
export function shortPrice(price: string, digits: number): string {
  const m = parse(price);
  if (m === null) return price;
  const s = toFixed(m, digits);
  return s.startsWith("0.") ? s.slice(1) : s;
}

/** Вычисленные доллары продажи — до центов: «16.20971» не помещается на кнопку. */
function cents(usd: string): string {
  const m = parse(usd);
  return m === null ? usd : toFixed(m, 2);
}

/**
 * Комиссия для показа: центы, но НЕ в ноль. На краях цены она честно бывает
 * меньше цента, и «−$0.00» читалось бы как «комиссии нет» ровно там, где она
 * есть, — мелкая показывается четырьмя знаками.
 */
function feeText(usd: string): string {
  const m = parse(usd);
  if (m === null) return usd;
  return m < 10_000 ? toFixed(m, 4) : toFixed(m, 2);
}

/** Цвет прибыли: плюс зелёный, минус красный, ноль — приглушённый. */
function pnlColor(pnl: Micro): string {
  if (pnl === 0) return "var(--color-muted)";
  return pnl > 0 ? "var(--color-buy)" : "var(--color-sell)";
}
